import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import SegmentModal from "../../components/SegmentModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const normalize = value => (value || "").toString().toLowerCase();

const Segments = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/segments", { params: { pageNumber: 1 } });
      setRecords((Array.isArray(data?.segments) ? data.segments : []).filter(item => item && typeof item === "object" && item.id != null));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
      setRecords([]);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const visibleRecords = useMemo(() => {
    const q = normalize(searchParam);
    return records.filter(item => !q || normalize(item?.name).includes(q));
  }, [records, searchParam]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/segments/${id}`);
      toast.success("Segmento removido com sucesso");
      setRecords(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      toastError(err);
    }
    setConfirmOpen(false);
    setDeletingRecord(null);
  };

  return (
    <MainContainer>
      <SegmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        segmentId={selectedRecord?.id}
        onSave={fetchRecords}
      />

      <ConfirmationModal
        title={deletingRecord ? `Excluir ${deletingRecord.name}?` : "Confirmar exclusão"}
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeletingRecord(null);
        }}
        onConfirm={() => handleDelete(deletingRecord.id)}
      >
        Esta ação não pode ser revertida.
      </ConfirmationModal>

      <MainHeader>
        <Title>Segmentos</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Pesquisar"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" color="primary" onClick={() => {
            setSelectedRecord(null);
            setModalOpen(true);
          }}>
            Novo Segmento
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRecords.filter(item => item && typeof item === "object" && item.id != null).map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record?.name || ""}</TableCell>
                <TableCell>{record?.active ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => {
                    setSelectedRecord(record);
                    setModalOpen(true);
                  }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    setDeletingRecord(record);
                    setConfirmOpen(true);
                  }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <TableRowSkeleton key={i} columns={3} />
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Segments;
